/**
 * Nodes: headless workbenches the organisation runs agents, models and memory on. The list, what
 * waits for approval (join requests and nodes offering more than was approved) and the join keys,
 * as three views; a node opens as its own page in the form column (`?node=`), where its manifest,
 * tags, updates, who may use it and whose memory it reads are list groups.
 *
 * Who may use a node is shown as the API allows the viewer (`assignmentAccess`): an administrator
 * assigns to anyone, the owner of the project that owns the node only to that project, an operator
 * only reads.
 */

import { capabilitiesOffered, type AssignmentScope, type NodeAssignment, type NodeCapability, type PendingNode, type SurfaceSelection } from '@aioe/shared';
import { dateShort } from '@aioe/shared/wording/dateTime';
import { DeviceOrders } from '../components/nodeOrdersPanel.tsx';
import { ArrowLeft, Clock, Cpu, Key } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { routes, type NodeRow } from '../api/client.ts';
import { ManifestView, parseTags, SurfaceGrowth } from '../components/nodes.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, selectClass, Stat, StatusMark, Tick, Toggle } from '../components/ui.tsx';
import { NodeMemoryReach } from '../components/memoryAccess.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { groupsAvailable } from '../directory.ts';
import { SubjectName, SubjectPicker } from '../components/SubjectPicker.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { useNames } from '../names.ts';
import { ProjectChips, ProjectPicker } from '../components/ProjectPicker.tsx';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { DeviceUpdates } from '../components/updates.tsx';
import { TeamsOnMachine } from '../components/teams/TeamsOnMachine.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

type Tab = 'nodes' | 'pending' | 'keys';
type Tone = NonNullable<typeof VIEW_TONES.nodes>;

/** A node's approval in words: colour is never the only signal. */
function Approval({ n }: { n: NodeRow }) {
  switch (n.approval.state) {
    case 'pending':
      return <StatusMark state="idle" label="Waiting for approval" />;
    case 'pending-surface':
      return <StatusMark state="medium" label="New surface to review" />;
    case 'refused':
      return <StatusMark state="disabled" label="Refused" />;
    default:
      return <StatusMark state={n.online ? 'online' : 'offline'} />;
  }
}

async function attempt(setError: (e?: string) => void, work: () => Promise<unknown>, done: () => void) {
  setError(undefined);
  try {
    await work();
    done();
  } catch (e) {
    setError((e as Error).message);
  }
}

export function NodesPage() {
  const tone = VIEW_TONES.nodes!;
  // The view and the open node are in the address (?tab=pending, ?node=…) so they can be linked to.
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'pending' || params.get('tab') === 'keys' ? (params.get('tab') as Tab) : 'nodes';
  const setTab = (t: Tab) => setParams(t === 'nodes' ? {} : { tab: t }, { replace: true });
  // One clock reading per mount: whether a key has expired need not tick while the page is open.
  const [now] = useState(() => Date.now());
  // The headline numbers come with one node asked for; the list pages itself, and an open node is asked for by id.
  const [rev, setRev] = useState(0);
  const nodes = useLoad(() => routes.nodesPage({ limit: 1 }), [rev], 15_000);
  const pending = useLoad(() => routes.pendingNodes().catch(() => [] as PendingNode[]), [], 15_000);
  const keys = useLoad(() => routes.joinKeysPage({ show: 'active', limit: 1 }), [rev]);
  const totals = nodes.data?.extra;
  const selected = params.get('node') ?? undefined;
  const open = useLoad(() => (selected ? routes.nodesPage({ id: selected, limit: 1 }) : Promise.resolve(undefined)), [selected, rev], 15_000);
  const surfaces = useLoad(() => (tab === 'pending' ? routes.nodesPage({ status: 'waiting', limit: 200 }) : Promise.resolve(undefined)), [tab, rev], 15_000);
  const waiting = (pending.data?.length ?? 0) + (totals?.waiting ?? 0);
  const reload = () => {
    setRev((r) => r + 1);
    pending.reload();
  };
  const node = open.data?.items[0];
  const [creatingKey, setCreatingKey] = useState(false);

  if (selected && node) {
    return (
      <PartBoundary key={`node-${node.deviceId}`} name="Node" tone={tone}>
        <NodePage key={node.deviceId} tone={tone} node={node} error={nodes.error} onBack={() => setParams({}, { replace: true })} onChange={reload} />
      </PartBoundary>
    );
  }

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Cpu />}
        title="Nodes"
        subtitle="Headless workbenches the organisation runs agents, models and memory on, each live only once approved."
        actions={
          <>
            <Button size="sm" variant="secondary" isPending={nodes.loading && Boolean(nodes.data)} onPress={reload}>
              Refresh
            </Button>
            {tab === 'keys' ? (
              <Button size="sm" variant="primary" onPress={() => setCreatingKey(true)}>
                New join key
              </Button>
            ) : null}
          </>
        }
      />
      <Explainer id="nodes" tone={tone} icon={<Cpu />} title="How nodes work" summary="A node joins with a code or a join key, and is live only once approved">
        <p>A node is a workbench with no one at it: a server or a spare machine that runs agents, serves models, classifies for data loss prevention or holds memory for the organisation.</p>
        <p>It joins by showing a code, or with a join key for automation. Until an administrator approves it, and approves what it offers, it does nothing. If it later starts offering more, the extra waits for approval while what was approved stays in force.</p>
        <p>An approved node is unused until it is assigned: to everyone, a project, a group or a person, with limits it enforces itself.</p>
      </Explainer>
      <div className="art-stats">
        <Stat tone={tone} icon={<Cpu />} label="Nodes" value={totals?.total} detail={totals ? `${totals.online} online` : ''} />
        <Stat tone={tone} icon={<Clock />} label="Waiting for you" value={waiting} detail="Join requests and new surfaces" needsYou={waiting > 0} onPress={() => setTab('pending')} selected={tab === 'pending'} />
        <Stat tone={tone} icon={<Cpu />} label="Agents running" value={totals?.agents} detail="Across every node" />
        <Stat tone={tone} icon={<Key />} label="Join keys" value={keys.data?.extra.active} detail="Active" onPress={() => setTab('keys')} selected={tab === 'keys'} />
      </div>
      <Segment aria-label="Nodes view" size="sm" selectedKey={tab} onSelectionChange={(k) => setTab(String(k) as Tab)} className="w-fit max-w-full">
        <Segment.Item id="nodes">Nodes</Segment.Item>
        <Segment.Item id="pending">Pending{waiting ? ` · ${waiting}` : ''}</Segment.Item>
        <Segment.Item id="keys">Join keys</Segment.Item>
      </Segment>
      <PartBoundary key="nodes" name="Nodes" tone={tone}>
        {tab === 'nodes' && <NodeList tone={tone} rev={rev} error={nodes.error} onOpen={(id) => setParams({ node: id }, { replace: false })} />}
      </PartBoundary>
      <PartBoundary key="pending" name="Pending nodes" tone={tone}>
        {tab === 'pending' && <PendingList tone={tone} rows={pending.data ?? []} surfaceRows={(surfaces.data?.items ?? []).filter((n) => n.approval.state === 'pending-surface' || n.approval.state === 'pending')} onChange={reload} />}
      </PartBoundary>
      <PartBoundary key="keys" name="Join keys" tone={tone}>
        {tab === 'keys' && <JoinKeys tone={tone} rev={rev} now={now} creating={creatingKey} onCreate={() => setCreatingKey(true)} onCloseCreate={() => setCreatingKey(false)} onChange={reload} />}
      </PartBoundary>
    </PageColumn>
  );
}

function NodeList({ tone, rev, error, onOpen }: { tone: Tone; rev: number; error?: string; onOpen: (id: string) => void }) {
  // Searched and filtered on the server, a page at a time; a failed refresh keeps the last good list with the error above it.
  const search = useListSearch();
  const [status, setStatus] = useState<'all' | 'online' | 'offline' | 'waiting'>('all');
  const query = { q: search.q, status: status === 'all' ? undefined : status, rev };
  const list = usePagedList((cursor) => routes.nodesPage({ q: search.q, ...(status === 'all' ? {} : { status }), cursor }), query, { intervalMs: 15_000, keyOf: (n) => n.deviceId });
  const rows = list.items;
  const loaded = list.loaded;
  const narrowed = search.q !== '' || status !== 'all';
  const projectName = useNames(rows.flatMap((n) => (n.owner.type === 'project' && n.owner.id ? [n.owner.id] : []))).projects;
  const nameOf = (id: string) => projectName.get(id) ?? id;
  return (
    <ListGroup
      title="Nodes"
      count={loaded ? listCount(rows.length, { one: 'node', many: 'nodes' }, { total: narrowed ? undefined : list.extra?.total, hasMore: list.hasMore, searched: narrowed }) : undefined}
      description="Open one to see what it offers, who may use it and whose memory it reads."
      actions={
        <>
          <SearchBox label="Search nodes" value={search.typed} onChange={search.setTyped} placeholder="Search nodes" className="w-40 max-w-full @lg:w-56" />
          <ChoiceSelect label="Status" value={status} options={{ all: 'Any status', online: 'Online', offline: 'Offline', waiting: 'Waiting for approval' }} onChange={setStatus} className="w-fit min-w-36 max-w-full" />
        </>
      }
      empty={loaded && rows.length === 0 && narrowed ? <NoMatch tone={tone} icon={<Cpu />} q={search.q} filtered={status !== 'all'} noun="nodes" onClear={() => (search.clear(), setStatus('all'))} /> : loaded && rows.length === 0 ? <Empty tone={tone} icon={<Cpu />} title="No nodes yet" body={`Run mojoup-node join --url ${window.location.origin} on the machine; it appears under Pending for approval.`} /> : undefined}
    >
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {!loaded && !error ? <ListBlock className="text-sm text-muted">Asking the nodes…</ListBlock> : null}
      {rows.map((n) => {
        const agents = n.load?.runningAgents ?? n.activeAgents;
        return (
          <ListRow
            key={n.deviceId}
            onPress={() => onOpen(n.deviceId)}
            icon={<Cpu />}
            title={n.capabilities?.name ?? n.hostname}
            description={
              <>
                <span className="block truncate" title={n.tags.join(', ')}>
                  {n.owner.type === 'project' ? `Project ${n.owner.id ? nameOf(n.owner.id) : ''}` : 'The organisation'}
                  {n.tags.length ? ` · ${n.tags.join(', ')}` : ''}
                </span>
                <span className="block truncate">
                  {agents ? `${agents} ${agents === 1 ? 'agent' : 'agents'} running` : 'No agents running'}
                  {n.sponsorUserId ? (
                    <>
                      {' · sponsored by '}
                      <SubjectName id={n.sponsorUserId} />
                    </>
                  ) : null}
                  {` · seen ${timeAgo(n.lastSeenAt)}`}
                </span>
              </>
            }
            status={<Approval n={n} />}
          />
        );
      })}
      {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
    </ListGroup>
  );
}

/** One node as a page in the form column: what it offers, its tags, updates, who may use it, whose memory it reads, its teams and its orders. */
function NodePage({ tone, node, error, onBack, onChange }: { tone: Tone; node: NodeRow; error?: string; onBack: () => void; onChange: () => void }) {
  const [tags, setTags] = useState(node.tags.join(', '));
  const [failed, setFailed] = useState<string>();
  const dirty = tags !== node.tags.join(', ');
  const orders = useLoad(() => routes.updateOrders().catch(() => []), [], 30_000);
  const overview = useLoad(() => routes.releases().catch(() => undefined), []);
  const ownerProjects = useNames(node.owner.type === 'project' ? [node.owner.id] : []);
  const ownerName = node.owner.type === 'project' ? (ownerProjects.projects.get(node.owner.id ?? '') ?? node.owner.id ?? '') : '';
  return (
    <PageColumn width="form">
      <PageHeader
        tone={tone}
        icon={<Cpu />}
        title={node.capabilities?.name ?? node.hostname}
        subtitle={`${node.owner.type === 'project' ? `A node of project ${ownerName}` : 'A node of the organisation'}, seen ${timeAgo(node.lastSeenAt)}.`}
        actions={
          <Button size="sm" variant="ghost" onPress={onBack}>
            <ArrowLeft className="size-4" />
            All nodes
          </Button>
        }
      />
      <ErrorNote error={error} />
      {node.approval.state === 'pending' ? (
        <ListGroup title="Not live" description="Its join key was revoked. Approve it to let it back on the mesh.">
          <ListRow
            title="Approve this node"
            status={<Approval n={node} />}
            control={
              <>
                <Button size="sm" variant="primary" onPress={() => void attempt(setFailed, () => routes.decideNode(node.deviceId, true), onChange)}>
                  Approve
                </Button>
                <Button size="sm" variant="danger-soft" onPress={() => void attempt(setFailed, () => routes.decideNode(node.deviceId, false), onChange)}>
                  Refuse
                </Button>
              </>
            }
          />
        </ListGroup>
      ) : null}
      <ListGroup title="What it offers" description="Its manifest, as approved.">
        <ListRow title="State" status={<Approval n={node} />} />
        {node.capabilities ? <ManifestView manifest={node.capabilities} /> : <ListBlock className="text-sm text-muted">It has not registered its manifest yet.</ListBlock>}
        <ListRow
          title="Tags"
          htmlFor="node-tags"
          description="Policies and assignments can name a node by its tags."
          control={
            <>
              <input id="node-tags" className={`${inputClass} w-64 max-w-full`} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tag:gpu, tag:team-a" />
              {dirty ? (
                <Button size="sm" variant="primary" onPress={() => void attempt(setFailed, () => routes.patchNode(node.deviceId, { tags: parseTags(tags) }), onChange)}>
                  Save tags
                </Button>
              ) : null}
            </>
          }
        />
        {failed ? (
          <ListBlock>
            <ErrorNote error={failed} />
          </ListBlock>
        ) : null}
      </ListGroup>
      {overview.data ? (
        <ListGroup title="Updates" description="The version it runs and any update pushed to it.">
          <DeviceUpdates
            device={node}
            orders={orders.data ?? []}
            overview={overview.data}
            reload={() => {
              orders.reload();
              onChange();
            }}
          />
        </ListGroup>
      ) : null}
      {node.assignmentAccess ? <Assignments node={node} access={node.assignmentAccess} /> : null}
      <ListGroup title="Reads memory as" description="The people whose project memory it may read while it runs their work.">
        <NodeMemoryReach nodeId={node.deviceId} editable />
      </ListGroup>
      <TeamsOnMachine tone={tone} teams={node.teams} />
      <DeviceOrders key={`orders-${node.deviceId}`} device={node} onChange={onChange} />
    </PageColumn>
  );
}

const CAPABILITY_TEXT: Record<NodeCapability, string> = {
  runtimes: 'Run agents',
  inference: 'Models',
  dlp: 'DLP',
  'memory:read': 'Read memory',
  sandbox: 'Sandboxes',
  computer: 'Computer use',
  'hosts:attach': 'Attach hosts',
};

function ScopeText({ s, projects }: { s: AssignmentScope; projects: ReadonlyMap<string, string> }) {
  if (s.type === 'tenant') return <>Everyone</>;
  if (s.type === 'project') return <>Project {projects.get(s.id) ?? s.id}</>;
  return (
    <>
      {s.type === 'group' ? 'Group ' : ''}
      <SubjectName id={s.id} />
    </>
  );
}

/** Who may use the node, and how much; the node enforces the quota. */
const CAPABILITY_HINT: Record<NodeCapability, string> = {
  runtimes: 'Start agent teams on it',
  inference: 'Call the models it serves',
  dlp: 'Classify through it',
  'memory:read': 'Read the memory it holds',
  sandbox: 'Run agents in its sandboxes',
  computer: 'Drive a browser or desktop on it',
  'hosts:attach': 'Attach to the workbench host on it',
};

const SCOPE_LABEL: Record<AssignmentScope['type'], string> = { project: 'Project', group: 'Group', user: 'Person', tenant: 'Everyone' };

/**
 * Who may use the node, and how much; the node enforces the quota. Shown as the API allows the viewer
 * (`access`): an administrator assigns to anyone, the owner of the project that owns the node only to
 * that project, an operator only reads.
 */
function Assignments({ node, access }: { node: NodeRow; access: NonNullable<NodeRow['assignmentAccess']> }) {
  const list = useLoad(() => routes.nodeAssignments(node.deviceId), [node.deviceId]);
  const offered = capabilitiesOffered(node.capabilities);
  const own = node.owner.type === 'project' ? node.owner.id : undefined;
  const [type, setType] = useState<AssignmentScope['type']>('project');
  const [id, setId] = useState(access === 'project' && own ? own : '');
  const [subjectLabel, setSubjectLabel] = useState<string>();
  const [caps, setCaps] = useState<NodeCapability[]>(offered.slice(0, 1));
  const [maxAgents, setMaxAgents] = useState('');
  const [fairShare, setFairShare] = useState(true);
  const [error, setError] = useState<string>();
  // An administrator searches for a project by name; anyone else is on few projects, so those they own are listed.
  const mine = useLoad(() => (access === 'manage' ? Promise.resolve([]) : routes.projects().catch(() => [])), [access]);
  const projectNames = useNames([...(list.data ?? []).flatMap((a) => (a.scope.type === 'project' ? [a.scope.id] : [])), own, id.trim() && type === 'project' ? id.trim() : undefined]).projects;
  // Someone who is not an administrator assigns only to a project they own, and only the node's own project when it has one.
  const ownedProjects = (mine.data ?? []).filter((p) => p.role === 'owner');
  const choosable = access === 'manage' ? [] : ownedProjects.filter((p) => !own || p.id === own);
  const mayRemove = (a: NodeAssignment) => access === 'manage' || (access === 'project' && a.scope.type === 'project' && ownedProjects.some((p) => a.scope.type === 'project' && p.id === a.scope.id));
  const scope: AssignmentScope = type === 'tenant' ? { type } : { type, id: id.trim() };
  const chosen = type === 'tenant' || Boolean(id.trim());
  const choose = (t: AssignmentScope['type']) => {
    setType(t);
    setId('');
    setSubjectLabel(undefined);
  };
  const add = () =>
    attempt(
      setError,
      () => routes.assignNode(node.deviceId, { scope, capabilities: caps, quota: maxAgents ? { maxConcurrentAgents: Number(maxAgents), fairShare } : fairShare ? { fairShare } : undefined }),
      () => {
        setId('');
        setSubjectLabel(undefined);
        list.reload();
      },
    );
  const rows: NodeAssignment[] = list.data ?? [];
  const limits = (a: NodeAssignment) => [a.quota?.maxConcurrentAgents ? `up to ${a.quota.maxConcurrentAgents} agents` : 'no limit', a.quota?.fairShare ? 'fair share' : ''].filter(Boolean).join(' · ');
  return (
    <ListGroup title="Who may use it" count={rows.length || undefined} description="A node is unused until it is assigned. The node enforces the limits.">
      {rows.map((a) => (
        <ListRow
          key={a.id}
          title={<ScopeText s={a.scope} projects={projectNames} />}
          description={
            <>
              <span className="block">{a.capabilities.map((c) => CAPABILITY_TEXT[c]).join(', ')}</span>
              <span className="block">{limits(a)}</span>
            </>
          }
          compactControl
          control={mayRemove(a) ? <RowMenu label={`More for this assignment`} items={[{ id: 'remove', label: 'Remove', danger: true }]} onAction={() => void attempt(setError, () => routes.unassignNode(node.deviceId, a.id), list.reload)} /> : undefined}
        />
      ))}
      {rows.length === 0 && access === 'read' ? <ListBlock className="text-sm text-muted">Nobody may use it yet.</ListBlock> : null}
      {access === 'read' && list.error ? (
        <ListBlock>
          <ErrorNote error={list.error} />
        </ListBlock>
      ) : null}
      {access === 'read' ? null : (
        <ListBlock className="grid gap-4">
          <div className="grid gap-1.5">
            <span className="text-xs font-medium text-foreground">Assign to</span>
            {access === 'manage' ? (
              <Segment aria-label="Assign to" size="sm" selectedKey={type} onSelectionChange={(k) => choose(String(k) as AssignmentScope['type'])} className="w-fit max-w-full">
                {(groupsAvailable() ? (['project', 'group', 'user', 'tenant'] as const) : (['project', 'user', 'tenant'] as const)).map((t) => (
                  <Segment.Item key={t} id={t}>
                    {SCOPE_LABEL[t]}
                  </Segment.Item>
                ))}
              </Segment>
            ) : (
              <p className="text-xs text-muted">{own ? 'Its own project. Assigning it more widely needs an administrator.' : 'A project you own. Assigning it more widely needs an administrator.'}</p>
            )}
            {type === 'tenant' && <p className="text-sm">Everyone in the organisation.</p>}
            {type === 'project' &&
              (choosable.length > 0 ? (
                <ChoiceSelect label="Project" placeholder="Choose a project" className="w-full" value={id || undefined} options={Object.fromEntries(choosable.map((p) => [p.id, p.name]))} onChange={setId} />
              ) : access === 'project' ? (
                <p className="text-sm">{own ? (projectNames.get(own) ?? own) : 'You own no project yet.'}</p>
              ) : (
                <div className="grid gap-2">
                  {id ? <ProjectChips ids={[id]} onRemove={() => setId('')} /> : <ProjectPicker onPick={(p) => setId(p.id)} />}
                </div>
              ))}
            {(type === 'group' || type === 'user') &&
              (id ? (
                <span className="inline-flex items-center gap-2 text-sm">
                  <SubjectName id={id} fallback={subjectLabel} />
                  <Button size="sm" variant="ghost" onPress={() => setId('')}>
                    Choose another
                  </Button>
                </span>
              ) : (
                <SubjectPicker
                  kinds={[type]}
                  onPick={(s) => {
                    setId(s.id);
                    setSubjectLabel(s.name);
                  }}
                />
              ))}
          </div>
          <fieldset className="grid gap-1.5">
            <legend className="mb-1.5 text-xs font-medium text-foreground">They may</legend>
            <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
              {offered.map((c) => (
                <Tick key={c} checked={caps.includes(c)} onChange={() => setCaps(caps.includes(c) ? caps.filter((x) => x !== c) : [...caps, c])}>
                  <span className="text-sm">
                    {CAPABILITY_TEXT[c]}
                    <span className="block text-xs text-muted">{CAPABILITY_HINT[c]}</span>
                  </span>
                </Tick>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-1.5">
            <span className="text-xs font-medium text-foreground">Limits</span>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <label className="inline-flex items-center gap-2">
                At most
                <input aria-label="Agents at once" className={`${selectClass} w-20 tabular-nums`} type="number" min={1} value={maxAgents} onChange={(e) => setMaxAgents(e.target.value)} placeholder="any" />
                agents at once
              </label>
              <Tick checked={fairShare} onChange={setFairShare}>
                Fair share between people
              </Tick>
            </div>
          </div>
          <ErrorNote error={error ?? list.error} />
          <div>
            <Button size="sm" variant="primary" isDisabled={caps.length === 0 || !chosen} onPress={() => void add()}>
              Assign
            </Button>
          </div>
        </ListBlock>
      )}
    </ListGroup>
  );
}

function PendingList({ tone, rows, surfaceRows, onChange }: { tone: Tone; rows: PendingNode[]; surfaceRows: NodeRow[]; onChange: () => void }) {
  if (rows.length === 0 && surfaceRows.length === 0) {
    return <Empty tone={tone} icon={<Clock />} title="Nothing waiting" body="Join requests and nodes that start offering more than was approved show here." />;
  }
  return (
    <>
      {rows.map((p) => (
        <PendingCard key={p.userCode} p={p} onChange={onChange} />
      ))}
      {surfaceRows
        .filter((n) => n.approval.state === 'pending-surface')
        .map((n) => (
          <SurfaceCard key={n.deviceId} node={n} onChange={onChange} />
        ))}
    </>
  );
}

/** A join request: what the node offers (clear what it should not), its tags, then Approve or Refuse in the group's last row. */
function PendingCard({ p, onChange }: { p: PendingNode; onChange: () => void }) {
  const [tags, setTags] = useState(p.tags.join(', '));
  const [selection, setSelection] = useState<SurfaceSelection>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const decide = async (approve: boolean) => {
    setBusy(true);
    await attempt(setError, () => (approve ? routes.approvePendingNode(p.userCode, { tags: parseTags(tags), surface: Object.keys(selection).length ? selection : undefined }) : routes.refusePendingNode(p.userCode)), onChange);
    setBusy(false);
  };
  return (
    <ListGroup
      title={`${p.name} asks to join`}
      description={`Asked ${timeAgo(p.createdAt)} · code ${p.userCode}${p.claimedEmail ? ` · passed on by ${p.claimedEmail}` : ''}. ${p.manifest ? 'Clear anything the node should not offer; the rest is what you approve.' : 'The node sent no manifest; it will be approved with nothing offered until it registers one and you review it.'}`}
    >
      {p.manifest ? <ManifestView manifest={p.manifest} selection={selection} onChange={setSelection} /> : null}
      <ListRow title="Tags" htmlFor={`pending-tags-${p.userCode}`} control={<input id={`pending-tags-${p.userCode}`} className={`${inputClass} w-64 max-w-full`} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tag:gpu" />} />
      <ListBlock className="grid gap-2">
        <ErrorNote error={error} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="primary" isPending={busy} onPress={() => void decide(true)}>
            Approve
          </Button>
          <Button size="sm" variant="danger-soft" isDisabled={busy} onPress={() => void decide(false)}>
            Refuse
          </Button>
        </div>
      </ListBlock>
    </ListGroup>
  );
}

function SurfaceCard({ node, onChange }: { node: NodeRow; onChange: () => void }) {
  const [error, setError] = useState<string>();
  return (
    <ListGroup title={`${node.capabilities?.name ?? node.hostname} offers more`} description="What was approved stays in force until you decide.">
      {node.surfaceDiff ? (
        <ListBlock>
          <SurfaceGrowth diff={node.surfaceDiff} />
        </ListBlock>
      ) : null}
      <ListBlock className="grid gap-2">
        <ErrorNote error={error} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="primary" onPress={() => void attempt(setError, () => routes.decideSurface(node.deviceId, true), onChange)}>
            Approve
          </Button>
          <Button size="sm" variant="secondary" onPress={() => void attempt(setError, () => routes.decideSurface(node.deviceId, false), onChange)}>
            Keep as it was
          </Button>
        </div>
      </ListBlock>
    </ListGroup>
  );
}

function JoinKeys({ tone, rev, now, creating, onCreate, onCloseCreate, onChange }: { tone: Tone; rev: number; now: number; creating: boolean; onCreate: () => void; onCloseCreate: () => void; onChange: () => void }) {
  const [error, setError] = useState<string>();
  // Expired and revoked keys stay out of sight until asked for; the list is searched and paged on the server.
  const search = useListSearch();
  const [expired, setExpired] = useState(false);
  const query = { q: search.q, expired, rev };
  const list = usePagedList((cursor) => routes.joinKeysPage({ q: search.q, show: expired ? 'all' : 'active', cursor }), query, { keyOf: (k) => k.id });
  const rows = list.items;
  return (
    <>
      <ListGroup
        title="Join keys"
        count={list.loaded ? listCount(rows.length, { one: 'key', many: 'keys' }, { total: search.q || expired ? undefined : list.extra?.active, hasMore: list.hasMore, searched: search.q !== '' }) : undefined}
        description="A join key lets automation start nodes without a code; you are recorded as the approver of every node that uses it. Revoking a key sends its nodes back to pending."
        actions={
          <>
            <SearchBox label="Search join keys" value={search.typed} onChange={search.setTyped} placeholder="Search keys" className="w-40 max-w-full @lg:w-56" />
            <Tick checked={expired} onChange={setExpired}>
              Show expired
            </Tick>
          </>
        }
        empty={
          list.loaded && rows.length === 0 && search.q ? (
            <NoMatch tone={tone} icon={<Key />} q={search.q} noun="keys" onClear={search.clear} />
          ) : list.loaded && rows.length === 0 ? (
            <Empty
              tone={tone}
              icon={<Key />}
              title="No join keys"
              body="A join key lets automation start nodes without a code; you are recorded as the approver of every node that uses it."
              action={
                <Button size="sm" variant="secondary" onPress={onCreate}>
                  New join key
                </Button>
              }
            />
          ) : undefined
        }
      >
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
        {rows.map((k) => {
          const live = !k.revokedAt && Date.parse(k.expiresAt) > now;
          return (
            <ListRow
              key={k.id}
              title={k.name}
              description={
                <>
                  <span className="block truncate">
                    <span className="mono">{k.prefix}…</span>
                    {k.reusable ? ' · reusable' : ' · single use'}
                    {k.ephemeral ? ' · ephemeral' : ''}
                    {k.tags.length ? ` · ${k.tags.join(', ')}` : ''}
                  </span>
                  <span className="block tabular-nums">
                    Used {k.uses} {k.uses === 1 ? 'time' : 'times'}
                  </span>
                </>
              }
              status={k.revokedAt ? <StatusMark state="disabled" label="Revoked" /> : live ? <StatusMark state="online" label={`Until ${dateShort(k.expiresAt)}`} /> : <StatusMark state="offline" label="Expired" />}
              compactControl
              control={!k.revokedAt ? <RowMenu label={`More for ${k.name}`} items={[{ id: 'revoke', label: 'Revoke', danger: true }]} onAction={() => void attempt(setError, () => routes.revokeJoinKey(k.id), onChange)} /> : undefined}
            />
          );
        })}
        {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
      </ListGroup>
      {creating ? <NewJoinKey onClose={onCloseCreate} onChange={onChange} /> : null}
    </>
  );
}

/** A new join key, in a sheet; once made, the key is shown there once to copy. */
function NewJoinKey({ onClose, onChange }: { onClose: () => void; onChange: () => void }) {
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [hours, setHours] = useState(24);
  const [reusable, setReusable] = useState(false);
  const [ephemeral, setEphemeral] = useState(false);
  const [minted, setMinted] = useState<string>();
  const [error, setError] = useState<string>();
  const create = () =>
    attempt(
      setError,
      async () => setMinted((await routes.createJoinKey({ name, tags: parseTags(tags), expiresInHours: hours, reusable, ephemeral })).key),
      () => {
        setName('');
        onChange();
      },
    );
  return (
    <FormSheet
      width="md"
      title="New join key"
      description="Nodes that join with it are approved in your name. Revoking it later sends them back to pending."
      onClose={onClose}
      footer={
        minted ? (
          <Button size="sm" variant="primary" onPress={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button size="sm" variant="tertiary" onPress={onClose}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" isDisabled={!name.trim()} onPress={() => void create()}>
              Create key
            </Button>
          </>
        )
      }
    >
      {minted ? (
        <div className="grid gap-1.5 rounded-xl bg-surface-secondary p-3 text-sm">
          <span className="text-muted">Copy it now; it is not shown again.</span>
          <code className="mono break-all">{minted}</code>
        </div>
      ) : (
        <>
          <FormField label="Name">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="CI runners" autoFocus />
          </FormField>
          <FormField label="Tags" hint="Given to every node that joins with it.">
            <input className={inputClass} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tag:ci" />
          </FormField>
          <FormField label="Valid for" hint="Hours, up to 90 days.">
            <input className={`${inputClass} max-w-32 tabular-nums`} type="number" min={1} max={2160} value={hours} onChange={(e) => setHours(Math.max(1, Number(e.target.value) || 1))} />
          </FormField>
          <div className="flex items-center justify-between gap-4 text-sm">
            <span>
              Reusable
              <span className="block text-xs text-muted">More than one node may join with it.</span>
            </span>
            <Toggle checked={reusable} onChange={setReusable} label="Reusable" />
          </div>
          <div className="flex items-center justify-between gap-4 text-sm">
            <span>
              Ephemeral nodes
              <span className="block text-xs text-muted">A node that joins with it is forgotten once it stops and stays offline.</span>
            </span>
            <Toggle checked={ephemeral} onChange={setEphemeral} label="Ephemeral nodes" />
          </div>
          <ErrorNote error={error} />
        </>
      )}
    </FormSheet>
  );
}
